import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import {
  ArrowLeft, Trees, MapPin, Phone, Mail, User, Building2, Pencil, Loader2,
  Hash, Plus, Upload, FileText, Clock, ChevronRight, Download,
} from "lucide-react";
import { fmtMoney, STATUS_LABEL, STATUS_TONE, shortId } from "../workspace/util";
import { ParkFormDialog } from "./Parks";
import { PlotFormDialog } from "./Plots";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "plots",     label: "Plots" },
  { key: "jobs",      label: "Jobs" },
  { key: "documents", label: "Documents" },
  { key: "timeline",  label: "Timeline" },
];

export default function ParkRecord() {
  const { parkId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [park, setPark] = useState(null);
  const [plots, setPlots] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [docs, setDocs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [editing, setEditing] = useState(false);
  const [addingPlot, setAddingPlot] = useState(false);

  const load = useCallback(async () => {
    const { data } = await adminApi.get(`/assets/parks/${parkId}`);
    setPark(data);
    const [pl, jo, dc, tl] = await Promise.all([
      adminApi.get(`/assets/parks/${parkId}/plots`),
      adminApi.get(`/assets/parks/${parkId}/jobs`),
      adminApi.get(`/assets/parks/${parkId}/documents`),
      adminApi.get(`/assets/parks/${parkId}/timeline`),
    ]);
    setPlots(pl.data.items || []);
    setJobs(jo.data.items || []);
    setDocs(dc.data.items || []);
    setTimeline(tl.data.items || []);
  }, [parkId]);

  useEffect(() => { load().catch((e) => toast.error(e.response?.data?.detail || "Park not found")); }, [load]);

  if (!park) return <div className="text-[#475569] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const photoSrc = park.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${park.photo_url}` : null;

  return (
    <div data-testid="park-record-page" className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 -mb-4 sm:-mb-6 lg:-mb-8">
      {/* HEADER */}
      <header className="bg-[#071A33] text-white px-4 sm:px-6 lg:px-8 py-6 border-b border-black/30">
        <button onClick={() => navigate("/adminpanel/assets/parks")} className="text-[12px] text-white/65 hover:text-white inline-flex items-center gap-1.5 mb-4" data-testid="park-back">
          <ArrowLeft size={13}/> Parks
        </button>
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-32 h-24 rounded-lg overflow-hidden bg-white/[0.06] border border-white/10 shrink-0">
            {photoSrc ? <img src={photoSrc} className="w-full h-full object-cover" alt=""/> : <Trees size={28} className="text-white/30 m-auto"/>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-semibold">{park.operator_name || "Park"}</p>
            <h1 className="font-display text-[34px] lg:text-[40px] leading-tight tracking-tight text-white mt-1" data-testid="park-name">{park.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[13px] text-white/75">
              {[park.address_line, park.town, park.county, park.postcode].filter(Boolean).length > 0 && (
                <span className="flex items-center gap-1.5"><MapPin size={13} className="text-[#B88A2A]"/>{[park.address_line, park.town, park.county, park.postcode].filter(Boolean).join(", ")}</span>
              )}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 max-w-md">
              <HeaderStat label="Total plots" value={park.plot_count}/>
              <HeaderStat label="Open jobs" value={park.open_jobs} tone="amber"/>
              <HeaderStat label="Status" value={park.status} muted/>
            </div>
          </div>
          <button onClick={() => setEditing(true)} className="text-[12px] bg-white text-[#071A33] hover:bg-[#FFE7B6] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md font-semibold" data-testid="park-edit-btn">
            <Pencil size={13}/> Edit park
          </button>
        </div>
      </header>

      {/* TABS */}
      <nav className="bg-[#F7F4ED]/60 border-b border-[#E2DCD0] px-4 sm:px-6 lg:px-8 flex overflow-x-auto">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-[13px] px-5 py-3.5 border-b-2 transition whitespace-nowrap ${active ? "border-[#064E2E] text-[#071A33] font-semibold bg-white" : "border-transparent text-[#475569] hover:text-[#071A33]"}`}
              data-testid={`park-tab-${t.key}`}>
              {t.label}
              {t.key === "plots" && plots.length > 0 && <span className="ml-2 text-[10px] bg-[#0F766E] text-white rounded-full px-1.5 py-0.5">{plots.length}</span>}
              {t.key === "jobs"  && jobs.length  > 0 && <span className="ml-2 text-[10px] bg-[#B88A2A] text-white rounded-full px-1.5 py-0.5">{jobs.length}</span>}
            </button>
          );
        })}
      </nav>

      {/* TAB CONTENT */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {tab === "overview"  && <Overview park={park}/>}
        {tab === "plots"     && <PlotsTab park={park} plots={plots} onAdd={() => setAddingPlot(true)}/>}
        {tab === "jobs"      && <JobsTab jobs={jobs}/>}
        {tab === "documents" && <DocsTab parkId={parkId} docs={docs} onReload={load}/>}
        {tab === "timeline"  && <TimelineTab items={timeline}/>}
      </div>

      <ParkFormDialog park={editing ? park : null} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }}/>
      <PlotFormDialog plot={addingPlot ? { park_id: park.id } : null} onClose={() => setAddingPlot(false)} onSaved={() => { setAddingPlot(false); load(); }}/>
    </div>
  );
}

function HeaderStat({ label, value, tone, muted }) {
  return (
    <div className={`rounded-md px-3 py-2 border ${tone === "amber" ? "bg-[#B88A2A]/15 border-[#B88A2A]/30" : "bg-white/[0.05] border-white/10"}`}>
      <p className="text-[10px] tracking-[0.16em] uppercase text-white/45">{label}</p>
      <p className={`text-xl font-display tabular-nums mt-0.5 ${muted ? "text-white/80" : "text-white"}`}>{value ?? "—"}</p>
    </div>
  );
}

function Overview({ park }) {
  const rows = [
    ["Park name", park.name],
    ["Operator", park.operator_name || "—"],
    ["Address", park.address_line || "—"],
    ["Town", park.town || "—"],
    ["County", park.county || "—"],
    ["Postcode", park.postcode || "—"],
    ["Park manager", park.manager_name || "—"],
    ["Manager phone", park.manager_phone || "—"],
    ["Manager email", park.manager_email || "—"],
    ["Number of plots", park.plot_count],
    ["Open jobs", park.open_jobs],
  ];
  return (
    <div className="admin-panel p-5" data-testid="park-overview">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="border-b border-[#E2DCD0] pb-2">
            <dt className="text-[10px] tracking-[0.18em] uppercase text-[#475569]">{k}</dt>
            <dd className="text-[#071A33] mt-0.5">{v ?? "—"}</dd>
          </div>
        ))}
      </dl>
      {park.notes && (
        <div className="mt-4 pt-4 border-t border-[#E2DCD0]">
          <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] mb-1">Internal notes</p>
          <p className="text-sm text-[#071A33] whitespace-pre-wrap">{park.notes}</p>
        </div>
      )}
    </div>
  );
}

function PlotsTab({ park, plots, onAdd }) {
  return (
    <div className="admin-panel overflow-x-auto" data-testid="park-plots-tab">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2DCD0] bg-[#F7F4ED]/40">
        <p className="text-sm text-[#475569]">Plots on this park: <strong className="text-[#071A33]">{plots.length}</strong></p>
        <button onClick={onAdd} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55]" data-testid="park-plots-add">
          <Plus size={12}/> Add plot
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] tracking-[0.18em] uppercase text-[#475569] bg-[#F7F4ED]/60">
          <tr className="text-left">
            <th className="px-4 py-2">Plot</th>
            <th className="px-4 py-2">Owner</th>
            <th className="px-4 py-2">Make · Model · Year</th>
            <th className="px-4 py-2 text-right">Open jobs</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E2DCD0]">
          {plots.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-[#475569] italic">No plots linked yet.</td></tr>}
          {plots.map((p) => (
            <tr key={p.id} className="hover:bg-[#F7F4ED]/40" data-testid={`park-plot-row-${p.id}`}>
              <td className="px-4 py-2 font-medium text-[#071A33]"><Link to={`/adminpanel/assets/plots/${p.id}`} className="hover:underline">Plot {p.plot_number}</Link></td>
              <td className="px-4 py-2 text-[#475569]">{p.owner_name || "—"}</td>
              <td className="px-4 py-2 text-[#475569]">{[p.manufacturer, p.model, p.year].filter(Boolean).join(" · ") || "—"}</td>
              <td className="px-4 py-2 text-right tabular-nums">{p.open_jobs}</td>
              <td className="px-4 py-2"><StatusPill status={p.status}/></td>
              <td className="px-4 py-2 text-right">
                <Link to={`/adminpanel/assets/plots/${p.id}`} className="text-[#0F766E] text-[12px] inline-flex items-center gap-1 hover:underline">Open <ChevronRight size={11}/></Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobsTab({ jobs }) {
  if (jobs.length === 0) return <div className="admin-panel p-8 text-center text-[#475569] italic" data-testid="park-jobs-tab">No jobs linked to this park yet.</div>;
  return (
    <div className="admin-panel overflow-x-auto" data-testid="park-jobs-tab">
      <table className="w-full text-sm">
        <thead className="text-[10px] tracking-[0.18em] uppercase text-[#475569] bg-[#F7F4ED]/60">
          <tr className="text-left">
            <th className="px-4 py-2">Job ID</th>
            <th className="px-4 py-2">Customer</th>
            <th className="px-4 py-2">Plot</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2 text-right">Value</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E2DCD0]">
          {jobs.map((j) => {
            const tone = STATUS_TONE[(j.status || "NEW").toUpperCase()] || STATUS_TONE.NEW;
            return (
              <tr key={j.id} className="hover:bg-[#F7F4ED]/40">
                <td className="px-4 py-2 font-mono text-[#0F766E]"><Link to={`/adminpanel/workspace/${j.id}`} className="hover:underline">{shortId(j.id)}</Link></td>
                <td className="px-4 py-2 text-[#071A33]">{j.full_name || "—"}</td>
                <td className="px-4 py-2 text-[#475569]">{j.plot_number ? `Plot ${j.plot_number}` : "—"}</td>
                <td className="px-4 py-2"><span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded ${tone}`}>{STATUS_LABEL[j.status] || j.status}</span></td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(j.client_price_ex_vat ?? j.quote_value ?? 0)}</td>
                <td className="px-4 py-2 text-right">
                  <Link to={`/adminpanel/workspace/${j.id}`} className="text-[#0F766E] text-[12px] inline-flex items-center gap-1 hover:underline">Open <ChevronRight size={11}/></Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DocsTab({ parkId, docs, onReload }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("park_id", parkId);
      fd.append("file", file);
      await adminApi.post("/assets/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onReload();
    } catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-panel p-5" data-testid="park-docs-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#475569]">{docs.length} document{docs.length === 1 ? "" : "s"}</p>
        <label className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] cursor-pointer">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Upload document
          <input type="file" className="hidden" onChange={upload}/>
        </label>
      </div>
      {docs.length === 0 ? (
        <p className="text-center text-[#475569] italic py-6">No documents uploaded.</p>
      ) : (
        <ul className="divide-y divide-[#E2DCD0]">
          {docs.map((d) => (
            <li key={d.id} className="py-2 flex items-center justify-between text-sm">
              <span className="flex items-center gap-2"><FileText size={13} className="text-[#475569]"/>{d.filename}<span className="text-[10px] text-[#475569]">· {Math.round((d.size || 0) / 1024)} KB</span></span>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="text-[#0F766E] hover:underline inline-flex items-center gap-1"><Download size={12}/> Open</a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TimelineTab({ items }) {
  if (items.length === 0) return <div className="admin-panel p-8 text-center text-[#475569] italic" data-testid="park-timeline-tab">No activity yet.</div>;
  return (
    <ul className="admin-panel p-5 space-y-3" data-testid="park-timeline-tab">
      {items.map((a) => (
        <li key={a.id} className="flex items-start gap-3">
          <Clock size={13} className="text-[#475569] mt-0.5 shrink-0"/>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#071A33]">{a.summary}</p>
            <p className="text-[10px] text-[#475569] mt-0.5">{a.case_summary} · {new Date(a.at).toLocaleString("en-GB")}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function StatusPill({ status }) {
  const s = (status || "ACTIVE").toUpperCase();
  const tone = s === "ACTIVE" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#475569]/10 text-[#475569]";
  return <span className={`text-[10px] uppercase tracking-[0.1em] font-semibold px-2 py-0.5 rounded ${tone}`}>{s}</span>;
}
